import Navbar from '@/pages/home/components/Navbar';
import PageFooter from '@/pages/home/components/PageFooter';

import { Link } from 'react-router-dom';

type SessionType = 'opening' | 'session' | 'demo' | 'break' | 'panel' | 'closing';

interface ScheduleItem {
  time: string;
  duration: string;
  type: SessionType;
  title: string;
  speaker?: string;
  speakerTitle?: string;
  location?: string;
}

const schedule: ScheduleItem[] = [
  {
    time: '0:00',
    duration: '10 mins',
    type: 'opening',
    title: 'opening & Strategic Context',
    speaker: 'Arch. Simbo Akinola-Odusola',
    speakerTitle: 'Business Consultant & GNA Pacesetter Award Committee Member',
    location: 'Main Stage — Online',
  },
  {
    time: '0:10',
    duration: '15 mins',
    type: 'session',
    title: 'SESSION 1: AI for Daily Business Operations',
    speaker: 'Dr. Kayode Elusoji',
    speakerTitle: 'AI Research Engineer & Executive Director, GNA',
    location: 'Main Stage — Online',
  },
  {
    time: '0:25',
    duration: '15 mins',
    type: 'session',
    title: 'SESSION 2: AI Marketing, Sales & Customer Growth',
    speaker: 'GN. Olalekan Martins',
    speakerTitle: 'CEO - Living Gold Consulting',
    location: 'Main Stage — Online',
  },
  {
    time: '0:40',
    duration: '15 mins',
    type: 'session',
    title: 'SESSION 3: AI Strategy, Ecosystem & Future Readiness',
    speaker: 'Dr. Yakubu Kayode Saheed',
    speakerTitle: 'Regional Director — Africa & Middle East, i3Cert Inc.',
    location: 'Main Stage — Online',
  },
  {
    time: '0:55',
    duration: '5 mins',
    type: 'break',
    title: 'Break Session',
    location: 'Main Stage — Online',
  },
  {
    time: '1:00',
    duration: '35 mins',
    type: 'demo',
    title: 'LIVE DEMO: Run Your Business with AI in Real-Time',
    // tools: ['ChatGPT', 'Canva', 'WhatsApp Business'],
    location: 'Main Stage — Online',
  },
  {
    time: '1:35',
    duration: '20 mins',
    type: 'panel',
    title: 'Question & Answer Session',
    speaker: 'All speakers',
    location: 'Main Stage — Online',
  },
  {
    time: '1:55',
    duration: '5 mins',
    type: 'closing',
    title: 'Closing Remarks & Call to Action',
    speaker: 'Dr. Kayode Elusoji',
    speakerTitle: 'AI Research Engineer & Executive Director, GNA',
    location: 'Main Stage — Online',
  },
];



const typeConfig: Record<SessionType, { badge: string; badgeText: string; icon: string; dot: string }> = {
  opening: { badge: 'bg-gray-100 text-gray-600', badgeText: 'OPENING', icon: 'ri-flag-line', dot: 'bg-gray-400' },
  session: { badge: 'bg-green-100 text-green-700', badgeText: 'SESSION', icon: 'ri-mic-line', dot: 'bg-green-600' },
  demo: { badge: 'bg-green-700 text-white', badgeText: 'LIVE DEMO', icon: 'ri-settings-3-line', dot: 'bg-amber-500' },
  break: { badge: 'bg-gray-200 text-gray-600', badgeText: 'BREAK', icon: 'ri-cup-line', dot: 'bg-gray-300' },
  panel: { badge: 'bg-amber-500 text-white', badgeText: 'PANEL Q&A', icon: 'ri-question-answer-line', dot: 'bg-amber-500' },
  closing: { badge: 'bg-gray-800 text-white', badgeText: 'CLOSING', icon: 'ri-flag-2-line', dot: 'bg-gray-700' },
};

const SchedulePage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Page Header */}
      <div className="bg-gradient-to-br from-green-800 to-green-900 pt-28 pb-16 px-4 md:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-block px-4 py-1.5 rounded-full bg-white/10 border border-white/20 text-green-200 text-xs font-semibold uppercase tracking-widest mb-4">
            Event Agenda
          </span>
          <h1 className="text-3xl md:text-5xl font-extrabold text-white mb-4">
            Summit Schedule
          </h1>
          <p className="text-green-200/80 text-base md:text-lg max-w-xl mx-auto">
            A focused, time-based agenda for May 30th, 2026. All times shown in Chicago CST.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 mt-6">
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20">
              <i className="ri-calendar-line text-amber-400"></i>
              <span className="text-white text-sm font-semibold whitespace-nowrap">May 30th, 2026</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20">
              <i className="ri-time-line text-amber-400"></i>
              <span className="text-white text-sm font-semibold whitespace-nowrap">12:30 PM – 2:30 PM CST</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20">
              <i className="ri-global-line text-amber-400"></i>
              <span className="text-white text-sm font-semibold whitespace-nowrap">Live Online</span>
            </div>
          </div>
        </div>
      </div>

      {/* Time Zone Reference */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-5">
          <div className="flex flex-wrap items-center justify-center gap-6">
            <span className="text-gray-500 text-sm font-semibold uppercase tracking-wider">Time Zones:</span>
            <div className="flex items-center gap-2">
              <span>🇺🇸</span>
              <span className="text-gray-700 text-sm font-medium">Chicago CST: 12:30 PM – 2:30 PM</span>
            </div>
            <div className="w-px h-4 bg-gray-300 hidden sm:block"></div>
            <div className="flex items-center gap-2">
              <span>🇳🇬</span>
              <span className="text-gray-700 text-sm font-medium">Nigeria WAT: 6:30 PM – 8:30 PM</span>
            </div>
            <div className="w-px h-4 bg-gray-300 hidden sm:block"></div>
            <div className="flex items-center gap-2">
              <span>🇬🇧</span>
              <span className="text-gray-700 text-sm font-medium">UK BST: 6:30 PM – 8:30 PM</span>
            </div>
          </div>
        </div>
      </div>

      {/* Schedule Timeline */}
      <main className="max-w-4xl mx-auto px-4 md:px-8 py-16">
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-[28px] md:left-[36px] top-0 bottom-0 w-0.5 bg-gradient-to-b from-green-600 via-amber-400 to-green-500"></div>

          <div className="space-y-5">
            {schedule.map((item, idx) => {
              const cfg = typeConfig[item.type];
              return (
                <div key={idx} className="flex gap-4 md:gap-6 items-start">
                  {/* Dot */}
                  <div className="shrink-0 z-10 mt-4">
                    <div className={`w-[56px] md:w-[72px] h-[56px] md:h-[72px] flex flex-col items-center justify-center rounded-full bg-white border-2 border-green-600`}>
                      <span className="text-green-700 font-bold text-xs leading-none text-center">{item.time}</span>
                      <span className="text-gray-400 text-[9px] mt-0.5">{item.duration}</span>
                    </div>
                  </div>

                  {/* Card */}
                  <div className="flex-1 bg-white rounded-2xl border border-gray-200 p-5 hover:-translate-y-0.5 transition-transform duration-200">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${cfg.badge}`}>
                        {cfg.badgeText}
                      </span>
                    </div>
                    <h4 className="font-bold text-gray-900 text-base mb-2">{item.title}</h4>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                      {item.speaker && (
                        <div className="flex items-center gap-2">
                          <i className="ri-user-line text-green-600 text-sm shrink-0"></i>
                          <span className="text-gray-700 text-sm font-medium">{item.speaker}</span>
                        </div>
                      )}
                      {item.location && (
                        <div className="flex items-center gap-2">
                          <i className="ri-map-pin-line text-amber-500 text-sm shrink-0"></i>
                          <span className="text-gray-500 text-sm">{item.location}</span>
                        </div>
                      )}
                    </div>
                    {item.speakerTitle && (
                      <p className="text-gray-400 text-xs mt-1.5 italic">{item.speakerTitle}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Register CTA */}
        <div className="mt-16 text-center bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-10">
          <h3 className="text-2xl font-extrabold text-gray-900 mb-3">Ready to Join the Summit?</h3>
          <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto">Secure your free spot now and be part of this transformative AI business event.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/register"
              className="px-8 py-3.5 rounded-full bg-gradient-to-r from-green-700 to-green-800 text-white font-bold text-sm hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap"
            >
              Register Free →
            </Link>
            <Link
              to="/premium"
              className="px-8 py-3.5 rounded-full border-2 border-green-600 text-green-700 font-semibold text-sm hover:bg-green-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              Explore Premium – $100
            </Link>
          </div>
        </div>
      </main>

      <PageFooter />
    </div>
  );
};

export default SchedulePage;
